import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useCompanyContext } from '@/hooks/useCompanyContext';
import { toast } from '@/hooks/use-toast';

// Tipos centralizados
interface User {
  id: string;
  email: string;
  full_name?: string;
  phone?: string;
  is_admin?: boolean;
  roles?: Array<{
    name: string;
    code: string;
  }>;
}

interface Branch {
  id: string;
  name: string;
  code: string;
  address?: string;
  is_active: boolean;
  company_id: string;
}

interface CashStation {
  id: string;
  station_name: string;
  station_code: string;
  branch_id: string;
  min_cash_alert_amount?: number;
  max_cash_alert_amount?: number;
  is_active: boolean;
}

interface DataState {
  users: User[];
  branches: Branch[];
  cashStations: CashStation[];
  loading: {
    users: boolean;
    branches: boolean;
    cashStations: boolean;
  };
  error: {
    users: string | null;
    branches: string | null;
    cashStations: string | null;
  };
  lastUpdated: {
    users: Date | null;
    branches: Date | null;
    cashStations: Date | null;
  };
}

type DataAction = 
  | { type: 'SET_LOADING'; payload: { key: keyof DataState['loading']; value: boolean } }
  | { type: 'SET_ERROR'; payload: { key: keyof DataState['error']; value: string | null } }
  | { type: 'SET_USERS'; payload: User[] }
  | { type: 'SET_BRANCHES'; payload: Branch[] }
  | { type: 'SET_CASH_STATIONS'; payload: CashStation[] }
  | { type: 'ADD_USER'; payload: User }
  | { type: 'UPDATE_USER'; payload: { id: string; data: Partial<User> } }
  | { type: 'ADD_CASH_STATION'; payload: CashStation }
  | { type: 'UPDATE_CASH_STATION'; payload: { id: string; data: Partial<CashStation> } }
  | { type: 'DELETE_CASH_STATION'; payload: string }
  | { type: 'CLEAR_ALL' };

const initialState: DataState = {
  users: [],
  branches: [],
  cashStations: [],
  loading: {
    users: false,
    branches: false,
    cashStations: false,
  },
  error: {
    users: null,
    branches: null,
    cashStations: null,
  },
  lastUpdated: {
    users: null,
    branches: null,
    cashStations: null,
  },
};

function dataReducer(state: DataState, action: DataAction): DataState {
  switch (action.type) {
    case 'SET_LOADING':
      return {
        ...state,
        loading: { ...state.loading, [action.payload.key]: action.payload.value }
      };
    case 'SET_ERROR':
      return {
        ...state,
        error: { ...state.error, [action.payload.key]: action.payload.value }
      };
    case 'SET_USERS':
      return {
        ...state,
        users: action.payload,
        loading: { ...state.loading, users: false },
        error: { ...state.error, users: null },
        lastUpdated: { ...state.lastUpdated, users: new Date() }
      };
    case 'SET_BRANCHES':
      return {
        ...state,
        branches: action.payload,
        loading: { ...state.loading, branches: false },
        error: { ...state.error, branches: null },
        lastUpdated: { ...state.lastUpdated, branches: new Date() }
      };
    case 'SET_CASH_STATIONS':
      return {
        ...state,
        cashStations: action.payload,
        loading: { ...state.loading, cashStations: false },
        error: { ...state.error, cashStations: null },
        lastUpdated: { ...state.lastUpdated, cashStations: new Date() }
      };
    case 'ADD_USER':
      return {
        ...state,
        users: [...state.users, action.payload]
      };
    case 'UPDATE_USER':
      return {
        ...state,
        users: state.users.map(user => 
          user.id === action.payload.id 
            ? { ...user, ...action.payload.data }
            : user
        )
      };
    case 'ADD_CASH_STATION':
      return {
        ...state,
        cashStations: [...state.cashStations, action.payload]
      };
    case 'UPDATE_CASH_STATION':
      return {
        ...state,
        cashStations: state.cashStations.map(station => 
          station.id === action.payload.id 
            ? { ...station, ...action.payload.data }
            : station
        )
      };
    case 'DELETE_CASH_STATION':
      return {
        ...state,
        cashStations: state.cashStations.filter(station => station.id !== action.payload)
      };
    case 'CLEAR_ALL':
      return initialState;
    default:
      return state;
  }
}

interface DataContextType {
  state: DataState;
  // Users
  loadUsers: () => Promise<void>;
  addUser: (user: User) => void;
  updateUser: (id: string, data: Partial<User>) => void;
  // Branches
  loadBranches: () => Promise<void>;
  // Cash Stations
  loadCashStations: () => Promise<void>;
  addCashStation: (station: CashStation) => void;
  updateCashStation: (id: string, data: Partial<CashStation>) => void;
  deleteCashStation: (id: string) => void;
  // Utilities
  invalidateCache: (key?: keyof DataState['lastUpdated']) => void;
  isStale: (key: keyof DataState['lastUpdated'], maxAge?: number) => boolean;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

interface DataProviderProps {
  children: ReactNode;
}

export function DataProvider({ children }: DataProviderProps) {
  const [state, dispatch] = useReducer(dataReducer, initialState);
  const { user } = useAuth();
  const { selectedCompany } = useAuth();

  // Función centralizada para manejo de errores
  const handleError = (key: keyof DataState['error'], error: any, operation: string) => {
    console.error(`Error in ${operation}:`, error);
    const errorMessage = error.message || `Error al ${operation}`;
    dispatch({ type: 'SET_ERROR', payload: { key, value: errorMessage } });
    toast({
      title: "Error",
      description: errorMessage,
      variant: "destructive",
    });
  };

  // Validar si los datos están obsoletos (por defecto 5 minutos)
  const isStale = (key: keyof DataState['lastUpdated'], maxAge = 5 * 60 * 1000) => {
    const lastUpdate = state.lastUpdated[key];
    if (!lastUpdate) return true;
    return Date.now() - lastUpdate.getTime() > maxAge;
  };

  // Invalidar caché
  const invalidateCache = (key?: keyof DataState['lastUpdated']) => {
    if (key) {
      dispatch({ type: 'SET_ERROR', payload: { key: key as keyof DataState['error'], value: null } });
    } else {
      dispatch({ type: 'CLEAR_ALL' });
    }
  };

  // Cargar usuarios de la empresa
  const loadUsers = async () => {
    if (!selectedCompany?.id || state.loading.users) return;
    
    dispatch({ type: 'SET_LOADING', payload: { key: 'users', value: true } });
    
    try {
      // Get users from this company
      const { data: userRoles, error } = await supabase
        .from('user_roles')
        .select(`
          user_id,
          roles:role_id (
            name,
            code
          )
        `)
        .eq('company_id', selectedCompany.id)
        .eq('is_active', true);
      
      if (error) throw error;
      
      // Get unique user IDs
      const userIds = [...new Set(userRoles?.map(ur => ur.user_id) || [])];
      
      if (userIds.length === 0) {
        dispatch({ type: 'SET_USERS', payload: [] });
        return;
      }
      
      // Get profiles for these users
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('*')
        .in('user_id', userIds)
        .order('created_at', { ascending: false });
      
      if (profilesError) throw profilesError;
      
      // Group users with their roles and profiles
      const userMap = new Map();
      userRoles?.forEach(ur => {
        if (!userMap.has(ur.user_id)) {
          userMap.set(ur.user_id, {
            id: ur.user_id,
            profile: profiles?.find(p => p.user_id === ur.user_id),
            roles: []
          });
        }
        if (ur.roles) {
          userMap.get(ur.user_id).roles.push(ur.roles);
        }
      });
      
      const formattedUsers = Array.from(userMap.values()).map(user => {
        const profile = user.profile;
        
        return {
          id: user.id,
          email: profile?.email || 'Sin email',
          full_name: profile?.full_name || 'Sin nombre',
          phone: profile?.phone || 'Sin teléfono',
          is_admin: user.roles.some((r: any) => ['admin', 'super_admin'].includes(r.code)),
          roles: user.roles,
        };
      });
      
      dispatch({ type: 'SET_USERS', payload: formattedUsers });
    } catch (error) {
      handleError('users', error, 'cargar usuarios');
    }
  };

  // Cargar sucursales
  const loadBranches = async () => {
    if (!selectedCompany?.id || state.loading.branches) return;
    
    dispatch({ type: 'SET_LOADING', payload: { key: 'branches', value: true } });
    
    try {
      // Determinar privilegios sin consultar tablas protegidas directamente
      // Usamos funciones seguras (security definer) para evitar errores 401 por RLS
      const [{ data: isSuperAdminRes }, { data: isAdminOrManagerRes }] = await Promise.all([
        supabase.rpc('is_user_super_admin', { _user_id: user?.id }),
        supabase.rpc('user_has_role_codes_in_company', {
          _role_codes: ['admin', 'manager'],
          _company_id: selectedCompany.id,
        }),
      ]);

      const isSuperAdmin = Boolean(isSuperAdminRes);
      const isCompanyAdminOrManager = Boolean(isAdminOrManagerRes);

      // 🎯 Admin/Manager/Super Admin: ver TODAS las sucursales de la empresa
      if (isSuperAdmin || isCompanyAdminOrManager) {
        console.log('👔 Loading ALL branches for admin/manager:', {
          isSuperAdmin,
          isCompanyAdminOrManager,
          companyId: selectedCompany.id,
        });

        const { data, error } = await supabase
          .from('branches')
          .select('*')
          .eq('company_id', selectedCompany.id)
          .eq('is_active', true)
          .order('created_at', { ascending: false });

        if (error) throw error;
        dispatch({ type: 'SET_BRANCHES', payload: data || [] });
      } else {
        // Usuarios regulares: solo sucursales asignadas
        // Asegurar userId para evitar `eq.undefined`
        let userId = user?.id as string | undefined;
        if (!userId) {
          const { data: authData } = await supabase.auth.getUser();
          userId = authData?.user?.id;
        }
        if (!userId) throw new Error('No authenticated user');

        console.log('👤 Loading assigned branches for regular user:', userId);

        const { data: userBranches, error: userBranchesError } = await supabase
          .from('user_branches')
          .select(`
            branch:branches!fk_user_branches_branch_id(*)
          `)
          .eq('user_id', userId);

        if (userBranchesError) throw userBranchesError;

        // Filtrar sucursales por company_id y is_active
        const filteredBranches = userBranches
          ?.map((ub) => ub.branch)
          .filter(
            (branch) => branch && branch.company_id === selectedCompany.id && branch.is_active,
          ) || [];

        dispatch({ type: 'SET_BRANCHES', payload: filteredBranches as Branch[] });
      }
    } catch (error) {
      handleError('branches', error, 'cargar sucursales');
    }
  };

  // Cargar estaciones de caja
  const loadCashStations = async () => {
    if (!selectedCompany?.id || state.loading.cashStations) return;
    
    dispatch({ type: 'SET_LOADING', payload: { key: 'cashStations', value: true } });
    
    try {
      const { data, error } = await supabase
        .from('cash_stations')
        .select('*')
        .eq('company_id', selectedCompany.id)
        .eq('is_active', true)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      dispatch({ type: 'SET_CASH_STATIONS', payload: data || [] });
    } catch (error) {
      handleError('cashStations', error, 'cargar estaciones de caja');
    }
  };

  // Funciones para manipular datos
  const addUser = (user: User) => {
    dispatch({ type: 'ADD_USER', payload: user });
  };

  const updateUser = (id: string, data: Partial<User>) => {
    dispatch({ type: 'UPDATE_USER', payload: { id, data } });
  };

  const addCashStation = (station: CashStation) => {
    dispatch({ type: 'ADD_CASH_STATION', payload: station });
  };

  const updateCashStation = (id: string, data: Partial<CashStation>) => {
    dispatch({ type: 'UPDATE_CASH_STATION', payload: { id, data } });
  };

  const deleteCashStation = (id: string) => {
    dispatch({ type: 'DELETE_CASH_STATION', payload: id });
  };

  // Cargar datos iniciales y forzar recarga al cambiar de empresa (multi-tenant safe)
  useEffect(() => {
    if (selectedCompany?.id) {
      console.log('🏢 Company changed, forcing data reload for multi-tenant safety:', selectedCompany.id);
      // Limpiar datos previos para evitar filtrados de empresa anterior
      dispatch({ type: 'CLEAR_ALL' });
      // Cargar datos frescos sin depender de 'stale'
      loadUsers();
      loadBranches();
      loadCashStations();
    } else {
      dispatch({ type: 'CLEAR_ALL' });
    }
  }, [selectedCompany?.id]);

  // Configurar subscripciones en tiempo real
  useEffect(() => {
    if (!selectedCompany?.id) return;

    const channels: any[] = [];

    // Suscripción a cambios en user_roles
    const userRolesChannel = supabase
      .channel('user_roles_changes')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'user_roles',
          filter: `company_id=eq.${selectedCompany.id}`
        }, 
        () => {
          console.log('🔄 User roles changed, reloading users...');
          loadUsers();
        }
      )
      .subscribe();

    // Suscripción a cambios en perfiles
    const profilesChannel = supabase
      .channel('profiles_changes')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'profiles'
        }, 
        () => {
          console.log('🔄 Profiles changed, reloading users...');
          loadUsers();
        }
      )
      .subscribe();

    // Suscripción a cambios en estaciones de caja
    const cashStationsChannel = supabase
      .channel('cash_stations_changes')
      .on('postgres_changes', 
        { 
          event: '*', 
          schema: 'public', 
          table: 'cash_stations',
          filter: `company_id=eq.${selectedCompany.id}`
        }, 
        (payload) => {
          console.log('🔄 Cash stations changed:', payload);
          loadCashStations();
        }
      )
      .subscribe();

    channels.push(userRolesChannel, profilesChannel, cashStationsChannel);

    return () => {
      channels.forEach(channel => {
        supabase.removeChannel(channel);
      });
    };
  }, [selectedCompany?.id]);

  const value: DataContextType = {
    state,
    loadUsers,
    addUser,
    updateUser,
    loadBranches,
    loadCashStations,
    addCashStation,
    updateCashStation,
    deleteCashStation,
    invalidateCache,
    isStale,
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
}

// Export the useDataContext hook
export const useDataContext = () => {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useDataContext must be used within a DataProvider');
  }
  return context;
};